import { HttpErrorResponse } from '@angular/common/http';
import { computed, inject, Injectable } from '@angular/core';
import {
    catchAndThrowErrorAsProblemDetails,
    createQuery,
    isResolvedProblemDetails,
    type ResolvedProblemDetails,
} from '@quipex/core/utils';
import {
    BuildingsApiService,
    type BuildingOwnershipTransferResponse,
    type OwnershipTransferRecipientPreviewResponse,
    type StartBuildingOwnershipTransferRequest,
} from '@quipex/shared/contracts';
import { catchError, defer, EMPTY, tap, type Observable } from 'rxjs';
import {
    previewBuildingOwnershipTransferRecipient,
    startBuildingOwnershipTransfer,
} from './api';

@Injectable()
export class ServerState {
    //#region Dependency injection
    private readonly buildingsApi = inject(BuildingsApiService);
    //#endregion

    //#region State
    private readonly transferQuery = createQuery<
        BuildingOwnershipTransferResponse,
        ResolvedProblemDetails
    >();
    private readonly recipientPreviewQuery =
        createQuery<OwnershipTransferRecipientPreviewResponse>();

    public readonly isProcessing = computed(() =>
        this.transferQuery.isFetching()
    );
    public readonly recipientPreview = this.recipientPreviewQuery.data;
    public readonly isCheckingRecipient = this.recipientPreviewQuery.isFetching;
    public readonly hasRecipientPreviewError =
        this.recipientPreviewQuery.isError;
    //#endregion

    //#region Public methods
    public startRecipientPreview(): void {
        this.recipientPreviewQuery.setLoading();
    }

    public checkRecipientEmail(
        buildingId: string,
        email: string
    ): Observable<OwnershipTransferRecipientPreviewResponse> {
        this.startRecipientPreview();

        return previewBuildingOwnershipTransferRecipient(
            this.buildingsApi,
            buildingId,
            email
        ).pipe(
            tap((preview) => this.recipientPreviewQuery.setData(preview)),
            catchError((error: unknown) => {
                const status =
                    error instanceof HttpErrorResponse ||
                    isResolvedProblemDetails(error)
                        ? error.status
                        : null;
                if (status === 403) {
                    this.recipientPreviewQuery.setData({
                        state: 'unavailable',
                        organisationName: null,
                    });
                } else {
                    this.recipientPreviewQuery.setError(error);
                }

                return EMPTY;
            })
        );
    }

    public resetRecipientPreview(): void {
        this.recipientPreviewQuery.reset();
    }

    public startTransfer(
        buildingId: string,
        payload: StartBuildingOwnershipTransferRequest
    ) {
        return defer(() => {
            this.transferQuery.setLoading();

            return startBuildingOwnershipTransfer(
                this.buildingsApi,
                buildingId,
                payload
            ).pipe(tap((response) => this.transferQuery.setData(response)));
        }).pipe(
            catchAndThrowErrorAsProblemDetails(),
            tap({
                error: (error: ResolvedProblemDetails) =>
                    this.transferQuery.setError(error),
            })
        );
    }
    //#endregion
}
